import { isKeyInObject, createReverseMap } from 'helpers/utils';
import { getSelectedClient } from 'services/storage';

const permissions = {
  DASHBOARD_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: 'CRU',
    recipient: 'R',
  },
  DONATION_LIST_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: 'CRU',
    recipient: '',
  },
  PAGES_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: 'CRU',
    recipient: '',
  },
  PAGES_ROUTE_ALL: {
    super_admin: 'R',
    partner_admin: '',
    recipient: '',
  },
  DONATION_PAGES_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: 'CRU',
    recipient: '',
  },
  EVENT_PAGES_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: 'CRU',
    recipient: '',
  },
  RECOMMENDATION_PAGES_ROUTE: {
    super_admin: '',
    partner_admin: '',
    recipient: '',
  },
  RECOMMENDATION_PREMIUM: {
    super_admin: 'CRUD',
    partner_admin: 'CRU',
    recipient: '',
  },
  PAGES_DETAIL_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: 'CRU',
    recipient: '',
  },
  RECIPIENTS_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  COMMUNITY_PAGES_DETAIL_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  RECIPIENTS_DETAIL_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  STRIPE_ONBOARDING_REFRESH_ROUTE: {
    super_admin: 'R',
    partner_admin: '',
    recipient: 'R',
  },
  STRIPE_ONBOARDING_STATUS_ROUTE: {
    super_admin: 'R',
    partner_admin: '',
    recipient: 'R',
  },
  ADMINS_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  ADMINS_DETAIL_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  PAYOUTS_ROUTE: {
    super_admin: '',
    partner_admin: '',
    recipient: 'R',
  },
  RECIPIENTS_DONATION_LIST_ROUTE: {
    super_admin: '',
    partner_admin: '',
    recipient: 'R',
  },
  COMMUNITY_PAGES_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  COMMUNITY_PAGES_ROUTE_ALL: {
    super_admin: 'R',
    partner_admin: '',
    recipient: '',
  },
  SETTINGS_ROUTE: {
    super_admin: '',
    partner_admin: '',
    recipient: 'R',
  },
  SETTINGS_RECIPIENT_ADMINS_ROUTE: {
    super_admin: '',
    partner_admin: '',
    recipient: 'CRUD',
  },
  SHOPPING_CART_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  SHOPPING_CART_DETAIL_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  FILTERS_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
  FILTERS_DETAIL_ROUTE: {
    super_admin: 'CRUD',
    partner_admin: '',
    recipient: '',
  },
};

export const SUPER_ADMIN_ROLE = 'super_admin';
export const PARTNER_ADMIN_ROLE = 'partner_admin';
export const RECIPIENT_ADMIN_ROLE = 'recipient_admin';

export const getIsSuper = (role: string) => role === SUPER_ADMIN_ROLE;
export const getIsRecipient = (role: string) => role === RECIPIENT_ADMIN_ROLE;
export const getIsPartner = (role: string) => role === PARTNER_ADMIN_ROLE;

// Auth0 doesn't return roles in a meaningful order, so on a device with no stored
// role a super admin could otherwise land on the recipient dashboard.
const ROLE_PRIORITY = [SUPER_ADMIN_ROLE, PARTNER_ADMIN_ROLE, RECIPIENT_ADMIN_ROLE];

// Unknown roles are dropped so they never land on a dashboard or show in the role switcher.
export const sortRolesByPriority = (userRoles: string[]) =>
  ROLE_PRIORITY.filter(role => userRoles.includes(role));

export const has_permission = (
  key: string,
  action: string,
  role: string | null,
  subscriptionLevel?: string
) => {
  let keyPermission = key;
  if (!key || !role) return false;
  if (key === 'PAGES_ROUTE') {
    // Client ALL can only see pages
    if (getSelectedClient()?.id === 0) keyPermission = 'PAGES_ROUTE_ALL';
  }
  if (key === 'COMMUNITY_PAGES_ROUTE') {
    if (getSelectedClient()?.id === 0) keyPermission = 'COMMUNITY_PAGES_ROUTE_ALL';
  }
  if (key === 'RECOMMENDATION_PAGES_ROUTE') {
    if (subscriptionLevel === 'PREMIUM') keyPermission = 'RECOMMENDATION_PREMIUM';
  }
  const permission = permissions[keyPermission];
  if (getIsSuper(role)) return permission?.super_admin.includes(action);
  if (getIsRecipient(role)) return permission?.recipient.includes(action);
  if (getIsPartner(role)) return permission?.partner_admin.includes(action);
  return false;
};

const roleMapping = {
  super_admin: SUPER_ADMIN_ROLE,
  partner_admin: PARTNER_ADMIN_ROLE,
  recipient_admin: RECIPIENT_ADMIN_ROLE,
};

export const reversedRoleMapping = createReverseMap(new Map(Object.entries(roleMapping)));

const roleDisplayMapping = {
  [SUPER_ADMIN_ROLE]: 'Super Admin',
  [PARTNER_ADMIN_ROLE]: 'Partner',
  [RECIPIENT_ADMIN_ROLE]: 'Recipient',
};

export const getRoleFromAuth = (role: string) => {
  if (isKeyInObject(role, roleMapping)) {
    return roleMapping[role];
  }
  return '';
};

export const getRoleDisplay = (role: string) => {
  if (isKeyInObject(role, roleDisplayMapping)) {
    return roleDisplayMapping[role];
  }
  return '';
};
